import { Alert } from '../../Alert';
import {
  isAfter,
  isBefore,
  subDays,
  addDays,
  differenceInDays,
  format,
  differenceInCalendarDays,
  isSameDay,
} from 'date-fns';
import { sendAnalyticsError } from '../../../libs/analytics';

export const getEditedData = (initialData, editCases, setEditCases) => {
  const analyzedEditCases = analyzeEditCases(editCases);
  const { editCasesData, wasUnitsEdited, wasStartDateEdited, wereBothEdited } = analyzedEditCases;

  if (wereBothEdited) return getDataIfBothEdited(initialData, analyzedEditCases, setEditCases);
  if (wasUnitsEdited) return getDataIfUnitsEdited(initialData.startDate, editCasesData.units, editCasesData.didChange);
  if (wasStartDateEdited) return getDataIfStartDateEdited(initialData, editCasesData.startDate, editCasesData.didChange);
  return initialData;
};

const getDataIfBothEdited = (initialData, analyzedEditCases, setEditCases) => {
  const { editCases, editCasesData, wasUnitsEditedFirst } = analyzedEditCases;

  const getDataIfUnitsEditedFirst = () => {
    const { isNewStartDateAfterThreeDaysMore } = getDateConditionals(
      editCasesData.startDate,
      initialData.startDate,
      initialData.endDate,
      initialData.metadata[0]?.value
    );

    const dataAfterUnitsEdited = getDataIfUnitsEdited(editCasesData.startDate, editCasesData.units);
    const isNewEndDateAfterOriginal = isAfter(new Date(dataAfterUnitsEdited.endDate), new Date(initialData.endDate));

    if (isNewStartDateAfterThreeDaysMore && isNewEndDateAfterOriginal && !initialData.isRetrospective) {
      const newEditCases = [...editCases];
      const unitsEditCaseIndex = newEditCases.findIndex((editCase) => editCase.internalId === 'ET7');
      const newUnits = differenceInCalendarDays(new Date(initialData.endDate), new Date(editCasesData.startDate));
      const didChange = newEditCases[unitsEditCaseIndex].data.didChange;
      newEditCases[unitsEditCaseIndex] = {
        ...newEditCases[unitsEditCaseIndex],
        data: { units: newUnits, didChange },
      };
      setEditCases(newEditCases);
    }

    return dataAfterUnitsEdited;
  };

  const getDataIfStartDateEditedFirst = () => {
    const dataAfterStartDateEdited = getDataIfStartDateEdited(initialData, editCasesData.startDate);

    if (dataAfterStartDateEdited.units < editCasesData.units) {
      const newEditCases = [...editCases];
      const unitsEditCaseIndex = newEditCases.findIndex((editCase) => editCase.internalId === 'ET7');
      const didChange = newEditCases[unitsEditCaseIndex].data.didChange;
      newEditCases[unitsEditCaseIndex] = {
        ...newEditCases[unitsEditCaseIndex],
        data: { units: dataAfterStartDateEdited.units, didChange },
      };
      setEditCases(newEditCases);
    }

    return getDataIfUnitsEdited(dataAfterStartDateEdited.startDate, editCasesData.units);
  };

  return wasUnitsEditedFirst ? getDataIfUnitsEditedFirst() : getDataIfStartDateEditedFirst();
};

const getDataIfUnitsEdited = (startDate, newUnits, didChange) => {
  return {
    units: newUnits,
    endDate: format(addDays(new Date(startDate), newUnits), 'P'),
    startDate: startDate,
    didChange
  };
};

export const getDataIfStartDateEdited = (initialData, newStartDate, didChange) => {
  const {
    isSingleLine,
    initialRequest: isInitialRequest,
    isRetrospective,
    isApproved,
    startDate,
    endDate,
    units,
  } = initialData;

  // Edited data options
  const onlyStartDateChanged = { startDate: newStartDate, endDate, units, didChange };
  const endDateChanged = {
    ...onlyStartDateChanged,
    endDate: units && format(addDays(new Date(newStartDate), units), 'P'),
  };
  const unitsChanged = {
    ...onlyStartDateChanged,
    units: endDate && differenceInDays(new Date(endDate), new Date(newStartDate)),
  };
  // End - Edited data options

  const { isNewStartDateAfterThreeDaysMore } = getDateConditionals(newStartDate, startDate, endDate, initialData.metadata[0]?.value);

  const getValuesSingleLine = () => {
    const getValuesRetrospective = () => {
      return isApproved ? endDateChanged : unitsChanged;
    };

    const getValuesNotRetrospective = () => {
      if (isApproved) return isNewStartDateAfterThreeDaysMore ? unitsChanged : endDateChanged;
      return endDateChanged;
    };

    return isRetrospective ? getValuesRetrospective() : getValuesNotRetrospective();
  };

  const getValuesMultiline = () => {
    if (isInitialRequest && isApproved) return unitsChanged;
  };

  return isSingleLine ? getValuesSingleLine() : getValuesMultiline();
};

export const analyzeEditCases = (editCases) => {
  const editCasesData = editCases.reduce((editCasesObject, editCase) => ({ ...editCasesObject, ...editCase.data }), {});
  const wasStartDateEdited = !!editCasesData.startDate;
  const wasUnitsEdited = !!editCasesData.units;
  const wereBothEdited = wasStartDateEdited && wasUnitsEdited;
  const editedCases = editCases.map((editCase) => editCase.internalId);
  const wasStartDateEditedFirst = wereBothEdited
    ? editedCases.findIndex((editedCase) => editedCase === 'ET6') <
      editedCases.findIndex((editedCase) => editedCase === 'ET7')
    : false;
  const wasUnitsEditedFirst = wereBothEdited && !wasStartDateEditedFirst;

  return {
    editCases,
    editCasesData,
    wasStartDateEdited,
    wasUnitsEdited,
    wereBothEdited,
    editedCases,
    wasStartDateEditedFirst,
    wasUnitsEditedFirst,
  };
};

const getDateConditionals = (newStartDate, initialStartDate, initialEndDate, amountOfDaysToSum) => {
  return {
    isNewStartDateAfterThreeDaysMore:
      initialStartDate && isAfter(new Date(newStartDate), addDays(new Date(initialStartDate), amountOfDaysToSum)),
    isNewStartDateAfterEndDate: initialEndDate && isAfter(new Date(newStartDate), new Date(initialEndDate)),
    isNewStartDateTheSameAsEndDate: initialEndDate && isSameDay(new Date(newStartDate), new Date(initialEndDate))
  }
}

export const validateEditedStartDate = (newStartDate, initialData, amountOfDaysToSum, toast) => {
  const { startDate, endDate } = initialData;
  const { isNewStartDateAfterEndDate, isNewStartDateTheSameAsEndDate } = getDateConditionals(
    newStartDate,
    startDate,
    endDate,
    amountOfDaysToSum
  );

  const validations = [
    {
      mustShowError: isNewStartDateTheSameAsEndDate,
      errorMessage: 'The total units for the request cannot be zero.',
      description: 'Please cancel this request.',
    },
    {
      mustShowError: isNewStartDateAfterEndDate,
      errorMessage: 'The start date cannot be later than the end date of the request.',
      description: 'Cancel this request and create a new request with the correct information.',
    }
  ];

  return validations.reduce((didFindErrors, { mustShowError, errorMessage, description }) => {
    if (mustShowError) {
      sendAnalyticsError('Edit Request', errorMessage);
      Alert(toast, errorMessage, description, 'error');
      return true;
    }
    return didFindErrors;
  }, false);
};
